import * as React from "react";

import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  type DialogSize,
} from "../../components/ui/dialog";
import {
  Drawer,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
} from "../../components/ui/drawer";
import { Icon, type IconName } from "../../components/ui/icon";
import type { Omittable } from "../../lib/props";
import { useOptionalAutoFocus } from "../../hooks/use-open-focus";
import {
  EntitySelector,
  EntitySelectorAutoTabs,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  filterEntityItems,
  useSelectorKeyboardNav,
  type EntityItemConfig,
  type EntityPoolConfig,
  type EntitySelection,
  type EntitySelectorConfig,
} from "../entity-selector";

/**
 * The synthetic create row's id. It is not an entity id, so the press is
 * intercepted before it can ever join a selection.
 */
const CREATE_ROW_ID = "entity-picker-create";

/**
 * The body's FIXED height in the dialog — nine spacious rows — which is what
 * makes the dialog a stable object. A cap alone is not: below it the popup
 * hugs its rows, so a picker is one height at six matches, another at one and
 * a third at none, and the footer jumps under the cursor with every keystroke.
 * Results change inside the frame; the frame does not change. The search field
 * sits above it as the popup's own fixed-height header row.
 */
const DIALOG_BODY_HEIGHT = 360;

/**
 * The same cap on a drawer, which has far more room to spend and a bottom edge
 * a thumb reaches. It is an upper bound, not a height: the drawer's own panel
 * is a bounded flex region, so a short screen shrinks the list rather than
 * pushing the confirm bar off the bottom.
 */
const DRAWER_BODY_MAX_HEIGHT = 720;

/**
 * Presentation is explicit, exactly as it is on the selector itself — there is
 * no automatic mobile swap.
 */
export type EntityPickerVariant = "dialog" | "drawer";

export type EntityPickerCreateAction = Omittable<{
  /** The footer button's label, in the caller's own noun ("Create Contact…"). */
  label: string;
  /**
   * Receives the typed query, so what the search failed to find opens
   * pre-named. `undefined` when nothing was typed: an empty string is a name,
   * and handing one over seeds a blank field with blankness.
   */
  onCreate: (query?: string) => void;
}>;

/**
 * What stands in the frame when the pool has nothing to offer BEFORE anything
 * is typed. There is no query to relax, so the copy states the fact and says
 * where the rows went — the caller knows whether the workspace has none yet or
 * everything it has is already linked, and those are different sentences. The
 * remedy is the picker's own `create` action, seated under the text.
 */
export type EntityPickerNoItems = Omittable<{
  icon: IconName;
  title: React.ReactNode;
  description?: React.ReactNode;
}>;

export type EntityPickerProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Pools, `maxSelections`, search placeholder and empty copy. */
  config: EntitySelectorConfig;
  /**
   * The selections the picker OPENS with. It edits a draft of them and nothing
   * reaches `onConfirm` until the confirm button is pressed, so a picker that
   * is cancelled changes nothing.
   */
  value?: EntitySelection[];
  onConfirm: (selections: EntitySelection[]) => void;
  /**
   * The pools filter server-side (they answer `onSearchChange` and set
   * `serverFiltered`). It changes what the list can honestly call itself: a
   * server page is a page of the matches, not all of them. @default false
   */
  serverSearch?: boolean;
  /** What heads the unsearched list of a server-searched pool. @default "Recent" */
  browseHeading?: string;
  /**
   * The page size a server-searched pool asks for. A full page means there are
   * probably more matches than are on screen, and the heading says so.
   */
  pageSize?: number;
  /**
   * The confirm button's label. Pass a function to name what is being added
   * and how many ("Add 3 contacts") — the count is the draft's size.
   * @default "Add"
   */
  confirmLabel?: string | ((count: number) => string);
  /** @default "Cancel" */
  cancelLabel?: string;
  /**
   * Offers to create what the search could not find, in two places: a footer
   * button that stands whether or not anything is typed, and a row at the end
   * of a searched list, which is where the eye already is when a search comes
   * up short.
   */
  create?: EntityPickerCreateAction;
  /**
   * The empty pool's state. Without it the selector's own one-line fallback
   * (`config.noItemsMessage`, then `config.emptyMessage`) stands alone in the
   * frame. A search that misses is unaffected either way: with `create` it
   * heads the create row, without it `config.emptyMessage` speaks.
   */
  noItems?: EntityPickerNoItems;
  /** @default "dialog" */
  variant?: EntityPickerVariant;
  /** Dialog width. @default "2xl" */
  size?: DialogSize;
}>;

/**
 * How a list of entities heads itself, honestly — and whether it says it out
 * loud. The label always NAMES the listbox for assistive tech; `visible` is
 * whether it is drawn as the group heading over the rows.
 *
 * A client-filtered pool holds everything it could ever show, and the rows are
 * all on screen or a scroll away, so a count above them restates what the eye
 * already has and rewrites itself on every keystroke. It keeps the pool's own
 * name for the reader who cannot see the rows, and draws nothing.
 *
 * A server-searched pool cannot state a total: it holds a page. Unsearched,
 * that page is not "everything" and saying so would be a lie — it is whatever
 * the pool chose to open with, which the caller names. Searched, a full page
 * means the matches ran past the edge of it. Both are things the rows cannot
 * say for themselves, so the group heading is drawn.
 *
 * A miss with a create row under it is the one case every pool labels: the
 * group heading says the search came up short, and the row beneath is the remedy.
 * Without a create row the pool renders nothing and the empty state speaks.
 */
function poolHeading({
  browseHeading,
  count,
  createRow,
  missHeading,
  pageSize,
  poolLabel,
  query,
  serverSearch,
}: {
  browseHeading: string;
  count: number;
  createRow: boolean;
  missHeading: string;
  pageSize: number | undefined;
  poolLabel: string;
  query: string;
  serverSearch: boolean;
}): { label: string; visible: boolean } {
  if (count === 0) return { label: missHeading, visible: createRow };
  if (!serverSearch) return { label: poolLabel, visible: false };
  if (!query) return { label: browseHeading, visible: true };
  if (pageSize != null && count >= pageSize) {
    return { label: `Top ${count} matches`, visible: true };
  }
  return { label: `${count} match${count === 1 ? "" : "es"}`, visible: true };
}

type EntityPickerBodyProps = Omit<EntityPickerProps, "open" | "size"> & {
  /**
   * The search field, so the overlay above can focus it once its own animation
   * has settled. See the drawer branch of `EntityPicker`.
   */
  inputRef: React.RefObject<HTMLInputElement | null>;
  variant: EntityPickerVariant;
};

/**
 * Everything inside the popup, so the draft is seeded fresh on every open: the
 * overlay unmounts its children when it closes, which is the reseed. A draft
 * held above the popup would open showing the last visit's abandoned edits.
 */
function EntityPickerBody({
  browseHeading = "Recent",
  cancelLabel = "Cancel",
  config,
  confirmLabel = "Add",
  create,
  description,
  inputRef,
  noItems,
  onConfirm,
  onOpenChange,
  pageSize,
  serverSearch = false,
  title,
  value,
  variant,
}: EntityPickerBodyProps) {
  const [draft, setDraft] = React.useState<EntitySelection[]>(value ?? []);
  const [search, setSearch] = React.useState("");
  const { ref, inputProps } = useSelectorKeyboardNav();

  const query = search.trim();
  const singleSelect = config.maxSelections === 1;

  const runCreate = React.useCallback(() => {
    onOpenChange(false);
    create?.onCreate(query || undefined);
  }, [create, onOpenChange, query]);

  // The create row rides in the list rather than beside it so the keyboard
  // reaches it: after a search that found nothing, ArrowDown then Enter is the
  // whole gesture. It is appended, never merged into the counts above — those
  // describe entities.
  const pickerConfig = React.useMemo<EntitySelectorConfig>(() => {
    if (!create || !query) return config;
    const createRow: EntityItemConfig = {
      actionRow: true,
      avatar: { icon: "plus" },
      id: CREATE_ROW_ID,
      // Curly quotes: the query is being shown back to the reader as words
      // they typed, not as a code literal.
      keywords: [query],
      reorderable: false,
      title: `Create “${query}”…`,
    };
    return {
      ...config,
      pools: config.pools.map((pool: EntityPoolConfig) => ({
        ...pool,
        items: [...pool.items, createRow],
      })),
    };
  }, [config, create, query]);

  const handleBeforeSelect = React.useCallback(
    (entity: EntityItemConfig, poolId: string) => {
      if (entity.id === CREATE_ROW_ID) {
        runCreate();
        return false;
      }
      // One selection is its own confirmation — there is nothing to review and
      // no count to build up, so the pick closes the picker. Vetoing rather
      // than letting the draft commit also answers re-picking the row that is
      // already selected, which changes no state and so would report nothing.
      if (singleSelect) {
        onConfirm([{ entity, id: entity.id, poolId }]);
        onOpenChange(false);
        return false;
      }
      return true;
    },
    [onConfirm, onOpenChange, runCreate, singleSelect],
  );

  const handleConfirm = React.useCallback(() => {
    onConfirm(draft);
    onOpenChange(false);
  }, [draft, onConfirm, onOpenChange]);

  // The create row is appended to `pickerConfig`, never to `config`, so this
  // count describes entities only — the row heading a miss is an act, not a
  // match.
  const createRow = !!create && !!query;
  const missHeading = config.emptyMessage ?? "No results found";
  const pools = config.pools.map((pool: EntityPoolConfig) => {
    const rendered = pool.serverFiltered
      ? pool.items
      : filterEntityItems(pool.items, search);
    const heading = poolHeading({
      browseHeading,
      count: rendered.length,
      createRow,
      missHeading,
      pageSize,
      poolLabel: pool.label ?? title,
      query,
      serverSearch,
    });
    return (
      <EntitySelectorPool
        key={pool.id}
        hideLabel={!heading.visible}
        label={heading.label}
        // Stable, because in the dialog the lane IS the body's right gutter
        // (see `body` below) — conditional, a short search result would jump
        // 10px wider than the browse list behind it. Inert in the drawer,
        // where the pool does not own its scroller.
        scrollbarGutter="stable"
        // The picker is a page-sized overlay, not a fixed-height popover —
        // the compact rhythm those were measured against reads as cramped
        // here, so every pool takes the sectioned list's roomier rows.
        rhythm="spacious"
        value={pool.id}
      />
    );
  });

  const list = (
    <EntitySelectorContent
      // The dialog (or drawer) is a sized host, not a menu popup, so the
      // inline content spans its 672px rather than holding the 280px picker
      // rung down its left edge — the project name, the address and the
      // facts would all truncate against empty space.
      //
      // The dialog pins BOTH bounds to one height (see `DIALOG_BODY_HEIGHT`);
      // the drawer is bounded by its own panel and only caps.
      maxHeight={
        variant === "drawer" ? DRAWER_BODY_MAX_HEIGHT : DIALOG_BODY_HEIGHT
      }
      minHeight={variant === "drawer" ? undefined : DIALOG_BODY_HEIGHT}
    >
      {/* The dialog mounts its search field as the popup's own header row
          instead. The drawer keeps the framed well here, focused from above
          once the open animation settles: focusing mid-animation raises the
          mobile keyboard, which resizes the visual viewport and desyncs the
          drawer's translate math into a top clip with the list locked. */}
      {variant === "drawer" && (
        <EntitySelectorInput {...inputProps} autoFocus={false} ref={inputRef} />
      )}
      {config.pools.length > 1 && <EntitySelectorAutoTabs />}
      {pools}
      {/* Rendered here, in the content's own tree, so the selector sees it
          and does not append its one-line fallback beside it. The create
          action repeats the footer's: the footer stands at the frame's edge,
          this one stands where the eye already is. */}
      {noItems && (
        <EntitySelectorEmpty
          // The frame is a dialog section, so the state takes the dialog rung:
          // the title is the section's headline over muted body copy, and the
          // glyph sits in the fanned chip the page-level empties wear — the
          // circle well vanishes against a dark popup fill.
          media="icon"
          size="default"
          noItems={{
            description: noItems.description,
            icon: noItems.icon,
            message: noItems.title,
            action: create ? (
              <Button onClick={runCreate} size="sm" variant="outline">
                <Icon name="plus" />
                {create.label}
              </Button>
            ) : undefined,
          }}
        />
      )}
    </EntitySelectorContent>
  );

  // The list insets itself by 8px. The dialog's content line is 16px — the
  // search-as-header popup is all list, so the dialog's default 24px gutter
  // reads as the rows floating away from the edges. The search glyph, rows,
  // section headings and footer all land on that one line (the footer joins it
  // below). On the right the body pays NO padding: the pool's stable scrollbar
  // lane (10px) is the gutter there, so the bar rides the popup edge outside
  // the rows instead of stacking a second inset over them. The drawer keeps
  // the symmetric 24px line its own header and footer sit on. Both bodies pay
  // an 8px bottom inset — the list's own step — because the footer opens with
  // its hairline, and without the inset the last row sits directly on it.
  const body =
    variant === "drawer" ? (
      <Box
        direction="column"
        flex={1}
        minHeight={0}
        paddingBottom="sm"
        paddingX="md"
      >
        {list}
      </Box>
    ) : (
      <Box
        direction="column"
        flex={1}
        minHeight={0}
        paddingBottom="sm"
        paddingLeft="sm"
      >
        {list}
      </Box>
    );

  // `me-auto` rather than `justify-between` on the row: between would spread
  // three buttons evenly and strand cancel in the middle, away from the confirm
  // it belongs beside. The margin pushes only this one to the far side.
  const createButton = create ? (
    <Button
      className="sm:me-auto"
      onClick={runCreate}
      size="sm"
      variant="ghost"
    >
      <Icon name="plus" />
      {create.label}
    </Button>
  ) : null;

  const confirmButton = singleSelect ? null : (
    <Button disabled={draft.length === 0} onClick={handleConfirm} size="sm">
      {typeof confirmLabel === "function"
        ? confirmLabel(draft.length)
        : confirmLabel}
    </Button>
  );

  const selector = (children: React.ReactNode) => (
    <EntitySelector
      config={pickerConfig}
      onBeforeSelect={handleBeforeSelect}
      onSearchChange={setSearch}
      onValueChange={setDraft}
      ref={ref}
      search={search}
      value={draft}
      variant="inline"
    >
      {children}
    </EntitySelector>
  );

  if (variant === "drawer") {
    return selector(
      <>
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
          {!!description && <DrawerDescription>{description}</DrawerDescription>}
        </DrawerHeader>
        <DrawerPanel variant="workspace">{body}</DrawerPanel>
        {/* No cancel: the swipe and the backdrop already dismiss a drawer, and
            a button restating them costs the row a thumb needs. */}
        <DrawerFooter>
          {createButton}
          {confirmButton}
        </DrawerFooter>
      </>,
    );
  }

  return selector(
    <>
      {/* The search field IS the header: the picker's noun already lives in
          its placeholder and on the confirm button, so a title row above it
          would only restate them. The title still names the dialog for
          assistive tech. */}
      <DialogTitle visuallyHidden>{title}</DialogTitle>
      {!!description && (
        <DialogDescription visuallyHidden>{description}</DialogDescription>
      )}
      <EntitySelectorInput {...inputProps} ref={inputRef} variant="header" />
      <DialogPanel variant="workspace">{body}</DialogPanel>
      {/* px-4: onto the picker's 16px content line, with the search glyph and
          the rows — the dialog's default 24px footer gutter is measured
          against a title row this popup does not have. */}
      <DialogFooter className="px-4">
        {createButton}
        <Button onClick={() => onOpenChange(false)} size="sm" variant="outline">
          {cancelLabel}
        </Button>
        {confirmButton}
      </DialogFooter>
    </>,
  );
}

/**
 * A searchable multi-select over one or more pools of entities, at the size of
 * a page rather than a menu: the picker a list reaches for when the popover
 * rung is too small to choose in.
 *
 * It is the entity selector's own list, its own rows and its own keyboard,
 * mounted inline inside a dialog (or a drawer) that adds a title, a draft, and
 * a confirm bar. So a row looks and behaves the same here as it does in the
 * 280px popover that opens off a chip.
 */
export function EntityPicker({
  onOpenChange,
  open,
  size = "2xl",
  variant = "dialog",
  ...body
}: EntityPickerProps): React.ReactElement {
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Typing narrows the list; it is not the drawer's point. The caret lands
  // once the open settles where a hardware keyboard is implied, and on a
  // coarse pointer the field waits for its own tap.
  const seatCaret = useOptionalAutoFocus();

  if (variant === "drawer") {
    return (
      <Drawer
        onOpenChange={onOpenChange}
        onOpenChangeComplete={(next: boolean) => {
          if (next && seatCaret) inputRef.current?.focus();
        }}
        open={open}
      >
        <DrawerPopup showBar>
          <EntityPickerBody
            {...body}
            inputRef={inputRef}
            onOpenChange={onOpenChange}
            variant="drawer"
          />
        </DrawerPopup>
      </Drawer>
    );
  }

  return (
    // Not responsive: phones render the Drawer branch above.
    <Dialog responsive={false} onOpenChange={onOpenChange} open={open}>
      {/* No corner ×: the popup's top row is the search field, and an
          absolutely-placed close button would sit on its tail. Escape and the
          footer's cancel both remain. */}
      <DialogPopup showCloseButton={false} size={size}>
        <EntityPickerBody
          {...body}
          inputRef={inputRef}
          onOpenChange={onOpenChange}
          variant="dialog"
        />
      </DialogPopup>
    </Dialog>
  );
}

EntityPicker.displayName = "EntityPicker";
